/**
 * Provided to the APP provider to use as the default router link component.
 *
 * This is necessary so Anchor components can perform SPA transitions.
 *
 * @see https://staging.atlassian.design/components/primitives/anchor/examples#router-links
 */

import type { RouterLinkComponentProps } from '@atlaskit/app-provider/router-link-provider';
import type { Ref } from 'react';
import { forwardRef } from 'react';
import type { LinkProps } from 'react-resource-router';
import { Link } from 'react-resource-router';

export type ReactResourceRouterLinkConfig = Pick<LinkProps, 'to' | 'href' | 'replace'>;

export const CustomRouterLink = forwardRef(
	(
		{ href, children, ...rest }: RouterLinkComponentProps<ReactResourceRouterLinkConfig>,
		ref: Ref<HTMLAnchorElement>,
	) => {
		// A basic link by passing a string as the component's `href` prop.
		if (typeof href === 'string') {
			return (
				<Link ref={ref} href={href} {...rest}>
					{children}
				</Link>
			);
		}

		// Gracefully handle undefined href — render as plain text with no navigation.
		if (!href) {
			return <span {...rest}>{children}</span>;
		}

		// Advanced link configuration by passing an object as the
		// component's `href` prop
		return (
			<Link ref={ref} href={href.href} to={href.to} replace={href.replace} {...rest}>
				{children}
			</Link>
		);
	},
);

CustomRouterLink.displayName = 'CustomRouterLink';
